import React, { useState, createContext, useCallback } from "react";
import { Fetch } from "../hooks/use-fetch-hook";

export const PrayerPartnerContext = createContext(null);

//this function handles the call logs and recordings that are displayed to prayer partner users
const extractBlacklistRows = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

export const PrayerPartnerProvider = ({children}) => {
    const [callLogs, setCallLogs] = useState(null);
    const [totalLogsCount, setTotalLogsCount] = useState(0);
    const [blacklistDetails, setBlacklistDetails] = useState([]);
    const [blacklistDetailsLoading, setBlacklistDetailsLoading] = useState(false);
    const fetchCallLogs = async(page,filters) => {
        const params = new URLSearchParams({
            direction: "incoming",
            page: String(page),
        });
        if (filters) {
            new URLSearchParams(filters).forEach((value, key) => {
                params.set(key, value);
            });
        }
        const url = `${process.env.REACT_APP_API_URL}/call_stats?${params.toString()}`;
        var options = {
            method: "GET",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
        };
        const result = await Fetch(url, options);
        setTotalLogsCount(result.data.total_count);
        setCallLogs(result.data.data);
        //return result.data;
    }
    const fetchTestimony = async(page) => {
        const url = `${process.env.REACT_APP_API_URL}/testimonies?page=${page}`;
        var options = {
            method: "GET",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
        };
        const result = await Fetch(url, options);
        return result.data;
    }

    /** GET /testimony — saved / reviewed testimonies (distinct from paginated /testimonies “new” queue). */
    const fetchReviewedTestimonies = async () => {
        const url = `${process.env.REACT_APP_API_URL}/testimony`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                Accept: "application/json",
            },
        };
        const result = await Fetch(url, options);
        return result.data;
    };
    
    const blockCaller = async(data) => {
        const url = `${process.env.REACT_APP_API_URL}/blacklist`;
        var options = {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
            body: JSON.stringify(data)
        };
        const result = await Fetch(url, options);
        return result;
    }

    const fetchBlacklistDetails = useCallback(async () => {
        const url = `${process.env.REACT_APP_API_URL}/blacklists/details`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                Accept: "application/json",
            },
        };
        setBlacklistDetailsLoading(true);
        const result = await Fetch(url, options);
        if (!result.error && result.data) {
            setBlacklistDetails(extractBlacklistRows(result.data));
        } else {
            setBlacklistDetails([]);
        }
        setBlacklistDetailsLoading(false);
        return result;
    }, []);

    const updateTestimonyStatus = async(taskSid) => {
        const url = `${process.env.REACT_APP_API_URL}/call_detail`;
        var options = {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
            body: JSON.stringify({ 
                id: taskSid,
                is_testimony: 2 
            })
        };
        const result = await Fetch(url, options);
        return result;
    }

    const removeTestimonyStatus = async(taskSid) => {
        const url = `${process.env.REACT_APP_API_URL}/call_detail`;
        var options = {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
            body: JSON.stringify({ 
                id: taskSid,
                is_testimony: 0 
            })
        };
        const result = await Fetch(url, options);
        return result;
    }

    const createTestimony = async(testimonyData) => {
        const url = `${process.env.REACT_APP_API_URL}/testimony`;
        var options = {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
                "Accept": 'application/json',
            },
            body: JSON.stringify(testimonyData)
        };
        const result = await Fetch(url, options);
        return result;
    }

    /**
     * POST /testimony/slack — server forwards { text } to Slack (browser cannot call hooks.slack.com: no CORS).
     * Body: { text: string, testimony_id?: number }
     */
    const postTestimonySlack = async ({ text, testimony_id }) => {
        const url = `${process.env.REACT_APP_API_URL}/testimony/slack`;
        const payload = { text };
        if (testimony_id != null) payload.testimony_id = testimony_id;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: process.env.REACT_APP_API_AUTHORIZATION,
            },
            body: JSON.stringify(payload),
        });
        const raw = await response.text();
        let data = null;
        try {
            data = raw ? JSON.parse(raw) : null;
        } catch {
            data = { message: raw };
        }
        if (!response.ok) {
            let msg =
                (data && (data.message || data.error || data.detail)) || "";
            if (!msg && data && data.errors && typeof data.errors === "object") {
                msg = JSON.stringify(data.errors);
            }
            if (!msg) msg = raw || `Request failed (${response.status})`;
            return { ok: false, message: String(msg).slice(0, 240) };
        }
        return { ok: true, data };
    };

    return (
        <PrayerPartnerContext.Provider 
            value=
            {{
                fetchCallLogs,
                callLogs,
                totalLogsCount,
                fetchTestimony,
                fetchReviewedTestimonies,
                blockCaller,
                fetchBlacklistDetails,
                blacklistDetails,
                blacklistDetailsLoading,
                updateTestimonyStatus,
                removeTestimonyStatus,
                createTestimony,
                postTestimonySlack,
            }}
        >
            {children}
        </PrayerPartnerContext.Provider>
    );
}
